import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useLang } from "@/hooks/use-language";
import { supabase } from "@/integrations/supabase/client";
import { JButton } from "@/components/JButton";
import { Plus, Pencil } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/")({
  component: KidsHome,
});

type Child = {
  id: string;
  name: string;
  age: number;
  age_tier: string;
  avatar_emoji: string;
  total_points: number;
  current_streak: number;
};

function KidsHome() {
  const { user } = useAuth();
  const { lang, dir } = useLang();
  const navigate = useNavigate();
  const [children, setChildren] = useState<Child[] | null>(null);
  const isAr = lang === "ar";

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: prof } = await supabase
        .from("profiles")
        .select("onboarded")
        .eq("id", user.id)
        .single();
      if (!prof?.onboarded) {
        navigate({ to: "/onboarding" });
        return;
      }
      const { data } = await supabase
        .from("children")
        .select("id, name, age, age_tier, avatar_emoji, total_points, current_streak")
        .order("created_at");
      setChildren((data as Child[]) ?? []);
    })();
  }, [user, navigate]);

  if (!children) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="size-10 rounded-full border-4 border-clay border-t-primary animate-spin" />
      </div>
    );
  }

  return (
    <div dir={dir} className="px-5 py-8 max-w-2xl mx-auto overflow-x-hidden">
      <p className="font-display-en text-xs uppercase tracking-[0.3em] text-primary font-bold mb-2 animate-fade-in">
        {isAr ? "من سيلعب؟" : "Who's playing?"}
      </p>
      <h1
        className={`${isAr ? "font-display-ar" : "font-display-en"} text-2xl sm:text-3xl md:text-4xl font-bold mb-8 tracking-tight animate-fade-in break-words`}
      >
        {isAr ? "اختر ملفًا" : "Choose a profile"}
      </h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {children.map((c, idx) => (
          <div key={c.id} className="relative animate-fade-in" style={{ animationDelay: `${idx * 80}ms`, animationFillMode: "backwards" }}>
            <Link
              to="/app/child/$childId"
              params={{ childId: c.id }}
              className="block bg-card border-2 border-clay/30 rounded-3xl p-6 shadow-xl hover:border-primary hover:-translate-y-1 active:scale-95 transition-all text-center"
            >
              <div className="text-6xl mb-3">{c.avatar_emoji}</div>
              <h3 className={`${isAr ? "font-display-ar" : "font-display-en"} text-xl font-bold mb-1`}>{c.name}</h3>
              <p className="text-xs text-foreground/60 mb-3">
                {isAr ? `العمر ${c.age}` : `Age ${c.age}`} · {c.age_tier}
              </p>
              <div className="flex items-center justify-center gap-3 text-sm">
                <span className="font-bold text-primary">⭐ {c.total_points}</span>
                <span className="text-foreground/40">·</span>
                <span className="font-bold text-secondary">🔥 {c.current_streak}</span>
              </div>
            </Link>
            <Link
              to="/app/child/$childId/edit"
              params={{ childId: c.id }}
              aria-label="Edit profile"
              className="absolute top-3 right-3 size-8 rounded-full bg-clay/40 hover:bg-primary/20 hover:text-primary flex items-center justify-center transition-colors"
            >
              <Pencil className="size-3.5" />
            </Link>
          </div>
        ))}

        <Link
          to="/app/child/new"
          className="bg-card border-2 border-dashed border-clay/50 rounded-3xl p-6 flex flex-col items-center justify-center hover:border-primary hover:bg-clay/10 transition-all text-foreground/60 min-h-[180px] animate-fade-in"
          style={{ animationDelay: `${children.length * 80}ms`, animationFillMode: "backwards" }}
        >
          <Plus className="size-8 mb-2" />
          <span className="font-bold text-sm">{isAr ? "إضافة طفل" : "Add child"}</span>
        </Link>
      </div>

      {children.length === 0 && (
        <div className="mt-6 text-center">
          <JButton onClick={() => navigate({ to: "/onboarding" })}>
            {isAr ? "أضف طفلك الأول" : "Add your first child"}
          </JButton>
        </div>
      )}
    </div>
  );
}
